'use client';

// FilePreviewList - Display uploaded files with extraction preview
// Supports PDF AI processing indicator and "Process with AI" option

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import LinearProgress from '@mui/material/LinearProgress';
import Paper from '@mui/material/Paper';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import { useTranslations } from 'next-intl';
import DeleteIcon from '@mui/icons-material/Delete';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DescriptionIcon from '@mui/icons-material/Description';
import TableChartIcon from '@mui/icons-material/TableChart';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';

/** Processing method used for PDF extraction */
export type ProcessingMethod = 'text' | 'ai';

export type FileStatus = 'uploading' | 'extracting' | 'ai-processing' | 'ready' | 'error';

export interface UploadedFile {
  /** Unique identifier for the file (local tracking ID) */
  id: string;
  /** Database content ID (for deletion sync) */
  contentId?: string | undefined;
  /** Original file name */
  fileName: string;
  /** File size in bytes */
  fileSize: number;
  /** MIME type of the file */
  mimeType: string;
  /** Current processing status */
  status: FileStatus;
  /** Preview of extracted content (first 500 chars) */
  preview?: string | undefined;
  /** Error message if extraction failed */
  errorMessage?: string | undefined;
  /** Upload/extraction progress (0-100) */
  progress?: number | undefined;
  /** Processing method used for PDFs ('text' or 'ai') */
  processingMethod?: ProcessingMethod | undefined;
}

export interface FilePreviewListProps {
  /** List of uploaded files */
  files: UploadedFile[];
  /** Callback to remove a file */
  onRemove: (fileId: string) => void;
  /** Whether remove buttons are disabled */
  disabled?: boolean | undefined;
  /** Callback to re-process a PDF with AI (optional) */
  onProcessWithAI?: ((fileId: string) => void) | undefined;
}

/**
 * Returns the appropriate icon component based on MIME type
 */
function getFileIcon(mimeType: string) {
  if (mimeType === 'application/pdf') {
    return <PictureAsPdfIcon sx={{ color: 'error.main' }} />;
  }
  if (mimeType.includes('wordprocessingml') || mimeType.includes('msword')) {
    return <DescriptionIcon sx={{ color: 'primary.main' }} />;
  }
  if (mimeType.includes('spreadsheetml') || mimeType.includes('excel')) {
    return <TableChartIcon sx={{ color: 'success.main' }} />;
  }
  return <InsertDriveFileIcon sx={{ color: 'text.secondary' }} />;
}

/**
 * Formats file size in human-readable format
 */
function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * FilePreviewList - Displays a list of uploaded files with their status and preview
 *
 * Features:
 * - File icon based on type (PDF, Word, Excel)
 * - File name and size display
 * - Progress indicator during upload/extraction
 * - Preview of extracted content
 * - Remove button for each file
 * - Error state display
 * - RTL support via CSS logical properties
 *
 * @example
 * ```tsx
 * <FilePreviewList
 *   files={uploadedFiles}
 *   onRemove={(id) => removeFile(id)}
 * />
 * ```
 */
export function FilePreviewList({
  files,
  onRemove,
  disabled = false,
  onProcessWithAI,
}: FilePreviewListProps) {
  const t = useTranslations('creator.fileUpload');
  const tPdfAI = useTranslations('creator.pdfAI');

  if (files.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        mt: 2,
      }}
    >
      {files.map((file) => (
        <Paper
          key={file.id}
          elevation={0}
          sx={{
            p: 2,
            border: '1px solid',
            borderColor: file.status === 'error' ? 'error.light' : 'divider',
            borderRadius: 1,
            backgroundColor: file.status === 'error' ? 'error.lighter' : 'background.paper',
          }}
        >
          {/* File header row */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
            }}
          >
            {/* File icon */}
            <Box sx={{ flexShrink: 0 }}>{getFileIcon(file.mimeType)}</Box>

            {/* File info */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="body2"
                fontWeight="medium"
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {file.fileName}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {formatFileSize(file.fileSize)}
              </Typography>
            </Box>

            {/* Status indicator and processing method */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
              {/* Processing method chip for PDFs - AI method */}
              {file.status === 'ready' &&
                file.mimeType === 'application/pdf' &&
                file.processingMethod === 'ai' && (
                  <Chip
                    size="small"
                    icon={<AutoFixHighIcon />}
                    label={tPdfAI('method.ai')}
                    variant="outlined"
                    color="primary"
                    sx={{ height: 24 }}
                  />
                )}
              {/* Processing method chip for PDFs - text method */}
              {file.status === 'ready' &&
                file.mimeType === 'application/pdf' &&
                file.processingMethod === 'text' && (
                  <Chip
                    size="small"
                    label={tPdfAI('method.text')}
                    variant="outlined"
                    sx={{ height: 24 }}
                  />
                )}
              {file.status === 'ready' && (
                <CheckCircleIcon sx={{ color: 'success.main', fontSize: 20 }} />
              )}
              {file.status === 'ai-processing' && (
                <CircularProgress size={20} />
              )}
              {file.status === 'error' && (
                <ErrorIcon sx={{ color: 'error.main', fontSize: 20 }} />
              )}
            </Box>

            {/* Remove button */}
            <IconButton
              size="small"
              onClick={() => onRemove(file.id)}
              disabled={
                disabled ||
                file.status === 'uploading' ||
                file.status === 'extracting' ||
                file.status === 'ai-processing'
              }
              aria-label={t('preview.remove')}
              sx={{
                flexShrink: 0,
                '&:hover': {
                  color: 'error.main',
                },
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* Progress bar for uploading/extracting */}
          {(file.status === 'uploading' || file.status === 'extracting') && (
            <Box sx={{ mt: 1.5 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                {file.status === 'uploading' ? t('preview.uploading') : t('preview.extracting')}
              </Typography>
              {file.progress !== undefined ? (
                <LinearProgress
                  variant="determinate"
                  value={file.progress}
                  sx={{ borderRadius: 1 }}
                />
              ) : (
                <LinearProgress
                  variant="indeterminate"
                  sx={{ borderRadius: 1 }}
                />
              )}
            </Box>
          )}

          {/* AI Processing indicator */}
          {file.status === 'ai-processing' && (
            <Box sx={{ mt: 1.5 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                {tPdfAI('processing')}
              </Typography>
              <LinearProgress variant="indeterminate" sx={{ borderRadius: 1 }} />
            </Box>
          )}

          {/* Error message */}
          {file.status === 'error' && file.errorMessage && (
            <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
              {t(`error.${file.errorMessage}`)}
            </Typography>
          )}

          {/* Content preview */}
          {file.status === 'ready' && file.preview && (
            <Box sx={{ mt: 1.5 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mb: 0.5,
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  {t('preview.extracted')} ({t('preview.characters', { count: file.preview.length })})
                </Typography>
                {/* Process with AI button - only show for PDFs with text extraction */}
                {onProcessWithAI &&
                  file.mimeType === 'application/pdf' &&
                  file.processingMethod === 'text' && (
                    <Button
                      size="small"
                      variant="text"
                      startIcon={<AutoFixHighIcon />}
                      onClick={() => onProcessWithAI(file.id)}
                      disabled={disabled}
                      sx={{ fontSize: '0.75rem', py: 0 }}
                    >
                      {tPdfAI('processWithAI')}
                    </Button>
                  )}
              </Box>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  backgroundColor: 'action.hover',
                  p: 1,
                  borderRadius: 1,
                  fontSize: '0.75rem',
                  lineHeight: 1.4,
                  maxHeight: 80,
                  overflow: 'hidden',
                  display: '-webkit-box',
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: 'vertical',
                }}
              >
                {file.preview}
              </Typography>
            </Box>
          )}
        </Paper>
      ))}
    </Box>
  );
}
